import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from './app.service';

export type TrialState = 'none' | 'active' | 'expired';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "2 days 4 hours", "3 hours 12 minutes" or "5 minutes": the two largest units
 * of a remaining time (minutes rounded up, so the last minute never reads 0).
 */
export function formatRemaining(ms: number, translate: TranslateService): string {
  const unit = (count: number, one: string, many: string) =>
    count === 1 ? translate.instant(one) : translate.instant(many, { count });
  const days = Math.floor(ms / DAY);
  const hours = Math.floor((ms % DAY) / HOUR);
  if (days > 0) {
    return [
      unit(days, '1 day', '[count] days'),
      ...(hours > 0 ? [unit(hours, '1 hour', '[count] hours')] : []),
    ].join(' ');
  }
  const minutes = Math.ceil((ms % HOUR) / MINUTE);
  if (hours > 0) {
    return [
      unit(hours, '1 hour', '[count] hours'),
      ...(minutes > 0 ? [unit(minutes, '1 minute', '[count] minutes')] : []),
    ].join(' ');
  }
  return unit(Math.max(minutes, 1), '1 minute', '[count] minutes');
}

/**
 * The instance's trial countdown, shared by the pre-join banner and the
 * notification list so both always agree. Derived from the startup app type
 * (`expiresAt` of a free instance), which the server re-sends over the
 * realtime channel when payment reports an upgrade or expiry. The server
 * enforces expiry itself; this is informational.
 */
@Injectable({ providedIn: 'root' })
export class TrialService {
  readonly #app = inject(AppService);
  readonly #translate = inject(TranslateService);
  readonly #now = signal(Date.now());
  readonly #lang = toSignal(this.#translate.onLangChange, { initialValue: null });

  /** The trial deadline (Unix ms), or `null` when the instance is not on a trial. */
  readonly endsAt = computed(() => {
    const type = this.#app.appType();
    return type?.name === 'free' ? type.expiresAt : null;
  });

  readonly state = computed<TrialState>(() => {
    const endsAt = this.endsAt();
    if (endsAt === null) return 'none';
    return this.#now() >= endsAt ? 'expired' : 'active';
  });

  readonly expired = computed(() => this.state() === 'expired');

  /** The translated notice, or `null` without a trial. Updated every minute. */
  readonly notice = computed(() => {
    this.#lang();
    const endsAt = this.endsAt();
    switch (this.state()) {
      case 'none':
        return null;
      case 'expired':
        return this.#translate.instant('Trial expired. Joining rooms is no longer possible.');
      case 'active':
        return this.#translate.instant('This instance is on a trial. Expires in [time].', {
          time: formatRemaining(endsAt! - this.#now(), this.#translate),
        });
    }
  });

  constructor() {
    // Tick only while a deadline exists; paid and standalone apps run no timer.
    effect((onCleanup) => {
      const endsAt = this.endsAt();
      if (endsAt === null) return;
      this.#now.set(Date.now());
      const tick = setInterval(() => this.#now.set(Date.now()), MINUTE);
      const remaining = endsAt - Date.now();
      // Flip to "expired" at the deadline itself, not up to a minute later.
      const deadline =
        remaining > 0 && remaining < 2 ** 31 - 1
          ? setTimeout(() => this.#now.set(Date.now()), remaining)
          : undefined;
      onCleanup(() => {
        clearInterval(tick);
        clearTimeout(deadline);
      });
    });
  }
}
